import { useEffect, useState } from 'react'
import { TabBar } from './components/TabBar'
import { AddReminder } from './screens/AddReminder'
import { Changelog } from './screens/Changelog'
import { Done } from './screens/Done'
import { Home } from './screens/Home'
import { HouseholdScreen } from './screens/Household'
import { MapView } from './screens/MapView'
import { JoinInvite, ResetPassword, VerifyEmail } from './screens/LinkLanding'
import { Onboarding } from './screens/Onboarding'
import { Settings } from './screens/Settings'
import { AdminConsole } from './screens/admin/AdminConsole'
import { parseAdminPath } from './lib/adminRoute'
import { api } from './lib/api'
import { trackKeyboardInset } from './lib/keyboard'
import { parseLinkPath, takeInvite } from './lib/linkRoute'
import { usePathname } from './lib/pathname'
import { useStore } from './lib/store'

export function App() {
  const {
    booted,
    user,
    onboarded,
    tab,
    setTab,
    boot,
    startWatching,
    stopWatching,
    refreshHouseholds,
    error,
    setError,
  } = useStore()
  const [adding, setAdding] = useState(false)
  const [showingDone, setShowingDone] = useState(false)
  const [showingChangelog, setShowingChangelog] = useState(false)
  const pathname = usePathname()
  const adminRoute = parseAdminPath(pathname)
  const linkRoute = parseLinkPath(pathname, window.location.search)

  useEffect(() => {
    void boot()
  }, [boot])

  // An invite link opened while signed out parks its code and sends them to
  // sign-in. This is the other half: redeem it the moment a session exists, so
  // accepting an invite never costs a second trip through the email.
  useEffect(() => {
    if (!user) return
    const code = takeInvite()
    if (!code) return
    void (async () => {
      try {
        await api.joinHousehold(code)
        await refreshHouseholds()
        setTab('household')
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
      }
    })()
  }, [user, refreshHouseholds, setTab, setError])

  // Publish how much of the screen the on-screen keyboard is covering, so forms
  // can scroll clear of it. Runs for the whole app life, signed in or not — the
  // sign-in form has the same problem as the category editor.
  useEffect(() => trackKeyboardInset(), [])

  // Start watching as soon as we have a session and permission already exists.
  useEffect(() => {
    if (!user) return
    startWatching()
    return () => stopWatching()
  }, [user, startWatching, stopWatching])

  if (!booted) {
    return (
      <div className="app">
        <div className="boot">
          <span className="boot__pin" />
        </div>
      </div>
    )
  }

  // Before the sign-in gate: these arrive from an email, in whatever browser
  // the mail client opens, and the token in the URL is the only credential the
  // flow needs. Bouncing a valid link to a sign-in wall is the worst outcome.
  if (linkRoute) {
    return (
      <div className="app">
        {linkRoute.kind === 'verify' && <VerifyEmail token={linkRoute.token} />}
        {linkRoute.kind === 'reset' && <ResetPassword token={linkRoute.token} />}
        {linkRoute.kind === 'join' && <JoinInvite code={linkRoute.code} />}
      </div>
    )
  }

  // Gating on `!user` alone ended onboarding the moment sign-up returned a
  // token, so the location and notification steps after it never rendered and
  // nothing ever asked for permission (APP-12). Onboarding owns the screen until
  // it says it is finished with this browser.
  if (!user || !onboarded) {
    // Landing on /admin while signed out shows the normal sign-in; the path is
    // still in the address bar afterwards, so the console opens on success.
    return (
      <div className="app">
        <Onboarding />
      </div>
    )
  }

  if (adminRoute) {
    return (
      <div className="app app--wide">
        <AdminConsole route={adminRoute} />
      </div>
    )
  }

  return (
    <div className="app">
      {error && (
        <button className="toast" onClick={() => setError(null)} role="alert">
          {error}
          <span>Dismiss</span>
        </button>
      )}

      {adding ? (
        <AddReminder
          onDone={() => setAdding(false)}
          onCancel={() => setAdding(false)}
          onUpgrade={() => {
            setAdding(false)
            setTab('profile')
          }}
        />
      ) : showingChangelog ? (
        <Changelog onBack={() => setShowingChangelog(false)} />
      ) : showingDone ? (
        <Done
          onBack={() => setShowingDone(false)}
          onUpgrade={() => {
            setShowingDone(false)
            setTab('profile')
          }}
        />
      ) : (
        <>
          {tab === 'list' && (
            <Home
              onAdd={() => setAdding(true)}
              onUpgrade={() => setTab('profile')}
              onShowDone={() => setShowingDone(true)}
              onShowVersion={() => setShowingChangelog(true)}
            />
          )}
          {tab === 'map' && <MapView />}
          {tab === 'household' && <HouseholdScreen />}
          {tab === 'profile' && <Settings />}
          <TabBar active={tab} onChange={setTab} />
        </>
      )}
    </div>
  )
}
